'use client'

import { type ICompanyLiteData } from '@repo/sdk'
import {
  Button,
  CountrySelect,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  PhoneInput,
} from '@repo/ui'
import { RotateCw } from 'lucide-react'
import { Control } from 'react-hook-form'

interface ICompanyFormFieldsProps {
  control: Control<ICompanyLiteData>
  onCreditSafeClick?: () => void
}

export const CompanyFormFields = ({ control, onCreditSafeClick }: ICompanyFormFieldsProps) => {
  return (
    <div className={'grid grid-cols-1 gap-2 py-4 md:grid-cols-2 md:gap-6'}>
      <FormField
        control={control}
        name="title"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Company name</FormLabel>
            <FormControl>
              <Input className={'mb-2'} {...field} placeholder="Company name" />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormField
        control={control}
        name="phone"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Phone number</FormLabel>
            <FormControl>
              <PhoneInput {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormField
        control={control}
        name="email"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Email</FormLabel>
            <FormControl>
              <Input className={'mb-2'} {...field} placeholder="Email" />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <div>
        <FormField
          control={control}
          name="account.vat"
          render={({ field }) => (
            <FormItem>
              <FormLabel>VAT no.</FormLabel>
              <div>
                <div className="relative mb-2 w-full">
                  <FormControl>
                    <Input
                      className="pr-12" // Add padding to prevent overlap with button
                      type="search"
                      {...field}
                      placeholder="VAT no."
                    />
                  </FormControl>
                  <Button
                    className="absolute right-2 top-1/2 -translate-y-1/2 p-2"
                    onClick={onCreditSafeClick}
                    type="button"
                    variant="ghost"
                  >
                    <RotateCw />
                  </Button>
                  <FormMessage />
                </div>
              </div>
            </FormItem>
          )}
        />
      </div>
      <div>
        <FormField
          control={control}
          name="account.registration_number"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Reg no.</FormLabel>
              <FormControl>
                <Input className={'mb-2'} {...field} placeholder="Reg no." />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>
      <div>
        <FormField
          control={control}
          name="address.address"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Address</FormLabel>
              <FormControl>
                <Input className={'mb-2'} {...field} placeholder="Address" />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>
      <div>
        <FormField
          control={control}
          name="address.city"
          render={({ field }) => (
            <FormItem>
              <FormLabel>City</FormLabel>
              <FormControl>
                <Input className={'mb-2'} {...field} placeholder="City" />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>
      <div>
        <FormField
          control={control}
          name="address.postcode"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Postcode</FormLabel>
              <FormControl>
                <Input className={'mb-2'} {...field} placeholder="Postcode" />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>
      <div>
        <FormField
          name="country.code"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Country</FormLabel>
              <FormControl>
                <CountrySelect {...field} value={field.value} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>
    </div>
  )
}
